import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack
} from '@mui/material';
import {
  MenuBook,
  ExpandMore,
  LightbulbOutlined,
  CalendarMonth,
  Home,
  LocalShipping,
  CheckCircle
} from '@mui/icons-material';

function MovingGuide() {
  const guides = [
    {
      title: '6-8 Wochen vor dem Umzug',
      icon: <CalendarMonth sx={{ color: '#55d6dc' }} />,
      tasks: [
        'Alten Mietvertrag kündigen',
        'Umzugsurlaub beim Arbeitgeber beantragen',
        'Umzugsunternehmen vergleichen und beauftragen',
        'Entrümpelung planen',
        'Nachmieter suchen (falls erforderlich)',
        'Kindergarten/Schule informieren'
      ]
    },
    {
      title: '4-6 Wochen vor dem Umzug',
      icon: <Home sx={{ color: '#55d6dc' }} />,
      tasks: [
        'Umzugskartons besorgen',
        'Halteverbotszone beantragen',
        'Versorgungsverträge kündigen/ummelden',
        'Nachsendeauftrag bei der Post',
        'Versicherungen informieren',
        'Renovierung planen'
      ]
    },
    {
      title: '2-4 Wochen vor dem Umzug',
      icon: <LocalShipping sx={{ color: '#55d6dc' }} />,
      tasks: [
        'Mit dem Packen beginnen',
        'Nicht täglich benötigte Sachen einpacken',
        'Möbel abmessen für neue Wohnung',
        'Putzplan erstellen',
        'Umzugshelfer organisieren',
        'Verpflegung für Umzugstag planen'
      ]
    },
    {
      title: 'Umzugstag',
      icon: <CheckCircle sx={{ color: '#55d6dc' }} />,
      tasks: [
        'Zählerstände ablesen und notieren',
        'Wohnungsübergabe alte Wohnung',
        'Umzugsgut kontrollieren',
        'Wohnungsübernahme neue Wohnung',
        'Wichtige Dokumente griffbereit halten',
        'Trinkgeld für Umzugshelfer bereithalten'
      ]
    }
  ];

  const tips = [
    {
      category: 'Verpackung',
      tips: [
        'Schwere Gegenstände in kleine Kartons',
        'Bücher nicht alle in einen Karton',
        'Kartons deutlich beschriften',
        'Zerbrechliches extra kennzeichnen',
        'Wichtige Dokumente separat transportieren'
      ]
    },
    {
      category: 'Organisation',
      tips: [
        'Raumplan für neue Wohnung erstellen',
        'Farbcodes für verschiedene Räume verwenden',
        'Inventarliste führen',
        'Fotos von wertvollen Gegenständen machen',
        'Erste-Hilfe-Karton packen'
      ]
    },
    {
      category: 'Kosten sparen',
      tips: [
        'Umzug in der Nebensaison planen',
        'Unter der Woche umziehen',
        'Selbst packen und nur Transport buchen',
        'Umzugskartons gebraucht kaufen',
        'Steuerliche Absetzbarkeit prüfen'
      ]
    }
  ];

  const commonMistakes = [
    {
      mistake: 'Zu spät mit der Planung beginnen',
      solution: 'Mindestens 2 Monate vorher anfangen'
    },
    {
      mistake: 'Umzugsvolumen unterschätzen',
      solution: 'Lieber zu viele Kartons als zu wenige'
    },
    {
      mistake: 'Keine Versicherung abschließen',
      solution: 'Transportversicherung ist wichtig'
    },
    {
      mistake: 'Alles selbst machen wollen',
      solution: 'Professionelle Hilfe spart Zeit und Nerven'
    },
    {
      mistake: 'Wichtige Ummeldungen vergessen',
      solution: 'Checkliste führen und abhaken'
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1f28 0%, #162a34 50%, #1c3440 100%)'
    }}>
      <Container maxWidth="lg" sx={{ pt: 12, pb: 6 }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <MenuBook sx={{ fontSize: 80, color: '#55d6dc', mb: 3 }} />
          <Typography variant="h2" sx={{ color: 'white', fontWeight: 'bold', mb: 3 }}>
            Umzugsratgeber
          </Typography>
          <Typography variant="h5" sx={{ color: '#55d6dc', mb: 2 }}>
            Ihr kompletter Guide für einen stressfreien Umzug
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, maxWidth: 800, mx: 'auto' }}>
            Von der ersten Planung bis zum Einzug – mit unserer Schritt-für-Schritt-Anleitung 
            vergessen Sie nichts und behalten den Überblick.
          </Typography>
        </Box>

        {/* Timeline Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Zeitplan für Ihren Umzug
          </Typography>
          {guides.map((phase, index) => (
            <Accordion
              key={index}
              sx={{
                mb: 2,
                bgcolor: '#1a3a44',
                color: 'white',
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: '#55d6dc' }} />}
                sx={{
                  '&:hover': { bgcolor: '#275664' }
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  {phase.icon}
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {phase.title}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {phase.tasks.map((task, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: '#55d6dc' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={task}
                        sx={{ color: 'white' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Tips & Tricks */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Tipps & Tricks
          </Typography>
          <Grid container spacing={4}>
            {tips.map((category, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{
                  bgcolor: '#1a3a44',
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%'
                }}>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                      <LightbulbOutlined sx={{ color: '#ffd700', fontSize: 30 }} />
                      <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                        {category.category}
                      </Typography>
                    </Stack>
                    <List>
                      {category.tips.map((tip, idx) => (
                        <ListItem key={idx} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircle sx={{ color: '#55d6dc', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={tip}
                            sx={{ color: 'white', opacity: 0.9 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Common Mistakes */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Häufige Fehler vermeiden
          </Typography>
          <Grid container spacing={3}>
            {commonMistakes.map((item, index) => (
              <Grid item xs={12} key={index}>
                <Paper
                  sx={{
                    p: 3,
                    bgcolor: '#275664',
                    border: '1px solid rgba(255,255,255,0.08)'
                  }}
                >
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: 'rgba(255,0,0,0.2)',
                          color: '#ff6b6b',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold'
                        }}>
                          ✗
                        </Box>
                        <Typography sx={{ color: 'white', fontWeight: 'bold' }}>
                          {item.mistake}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: 'rgba(85,214,220,0.2)',
                          color: '#55d6dc',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold'
                        }}>
                          ✓
                        </Box>
                        <Typography sx={{ color: '#55d6dc' }}>
                          {item.solution}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box sx={{ 
          bgcolor: 'rgba(255,215,0,0.1)', 
          border: '2px solid #ffd700',
          borderRadius: 3,
          p: 4,
          textAlign: 'center'
        }}>
          <Typography variant="h4" sx={{ color: 'white', mb: 2, fontWeight: 'bold' }}>
            Brauchen Sie professionelle Hilfe?
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, mb: 3 }}>
            Unser erfahrenes Team unterstützt Sie bei jedem Schritt Ihres Umzugs
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Box
              component="button"
              onClick={() => window.location.href = '/calculator'}
              style={{
                backgroundColor: '#55d6dc',
                color: '#275664',
                padding: '12px 32px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer'
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#4bc2c8')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#55d6dc')}
            >
              Umzug kalkulieren
            </Box>
            <Box
              component="button"
              onClick={() => window.location.href = '/contact'}
              style={{
                backgroundColor: 'transparent',
                color: '#ffd700',
                padding: '12px 32px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                border: '2px solid #ffd700',
                borderRadius: 4,
                cursor: 'pointer'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,215,0,0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Beratung anfordern
            </Box>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}

export default MovingGuide;